import EditLocationAltIcon from '@mui/icons-material/EditLocationAlt'
import {
  AccordionDetails,
  Box,
  Button,
  FormControlLabel,
  Grid,
  Switch,
  Typography,
} from '@mui/material'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { useGetContactByEntityIdQuery } from '../../../app/redux-fetch/apiCoreQuery'
import { formatDate } from '../../../app/utils'
import StyledAccordion from '../../../components/Accordions/StyledAccordion'
import StyledAccordionSummary from '../../../components/Accordions/StyledAccordionSummary'
import ActionButton from '../../../components/Buttons/ActionButton'
import DefaultSpacingGrid from '../../../components/Containers/DefaultSpacingGrid'
import FormDatePickerInput from '../../../components/Forms/FormDatePickerInput'
import FormInput from '../../../components/Forms/FormInput'
import FormSelect from '../../../components/Forms/FormSelect'
import commonMessages from '../../../components/Messages/commonMessages'
import InfoDisplayItem from '../../../components/Typographies/InfoDisplayItem'
import { LabelItem } from '../../../components/Typographies/LabelItem'
import { LoadType } from '../../../types/common'
import { useOrderFormContext } from '../context/OrderFormContext'
import { useLocationModal } from '../hooks/useLocationModal'
import { useOrderStatus } from '../hooks/useOrderStatus'
import EmptyContainerCollection from './EmptyContainerCollection/EmptyContainerCollection'
import FlowSection from './FlowSection'
import messages from './messages'
import ReturnTo from './ReturnTo'
import {
  getAvailableLocations,
  getLocationAddress,
  getLocationRevision,
  mapContactsToOptions
} from './utils'
import WaypointContainer from './WaypointContainer'

interface ShipFromFormProps {
  index: number
  disabled?: boolean
}

const ShipFromForm: React.FC<ShipFromFormProps> = ({ index, disabled = false }) => {
  const { control, setValue, watch, trigger } = useFormContext()
  const [expanded, setExpanded] = useState(true)
  const { formatMessage } = useIntl()
  const { variant } = useOrderFormContext()
  const { isCreated } = useOrderStatus()
  const handleToggleAccordion = () => {
    setExpanded((prev) => !prev)
  }

  const customerId = watch('customerId')

  const oldLocations = watch(`bookings.${index}.shipFrom.locationId`)
  const loadTypeId = watch(`bookings.${index}.loadTypeId`)

  const memoizedOldShipmentLocations = useMemo(() => {
    return oldLocations ? [oldLocations] : []
  }, [oldLocations])

  const { openModal, ModalComponent, locationOptions, locations } = useLocationModal({
    onSave: (id) => {
      setValue(`bookings.${index}.shipFrom.locationId`, id)
    },
    customerId,
    filterFunction: (location) => {
      return location.enabled && location.locationTypeId !== 0 && location.locationTypeId !== 1
    },
    isSelectLocationType: true,
    oldLocations: memoizedOldShipmentLocations,
    disabled,
  })

  const locationId = watch(`bookings.${index}.shipFrom.locationId`)
  const embeddedLocation = watch(`bookings.${index}.shipFrom.location`)

  const getLocationName = useCallback(
    (locationId: number) => {
      return (
        locationOptions.find((location) => location.value === locationId)?.label ??
        embeddedLocation?.name ??
        'N/A'
      )
    },
    [locationOptions, embeddedLocation]
  )

  const loadingDate = formatDate(watch(`bookings.${index}.shipFrom.loadingDate`))

  const { data: contacts } = useGetContactByEntityIdQuery(locationId, {
    skip: !locationId || disabled,
    refetchOnMountOrArgChange: true,
  })

  const getContactOptions = useCallback(() => {
    return mapContactsToOptions(contacts)
  }, [contacts])

  const consignments = watch('bookings') || []

  const currentConsignment = consignments[index]

  const usedLocationIds: number[] = []

  if (currentConsignment?.shipFrom?.locationId) {
    usedLocationIds.push(currentConsignment.shipFrom.locationId)
  }

  if (currentConsignment?.deliverTo?.locationId) {
    usedLocationIds.push(currentConsignment.deliverTo.locationId)
  }

  const selectedLocation = locations.find(
    (l: any) => l.id === locations.find((loc: any) => loc.id === locationId)?.id
  )

  const modeOfTransit = watch(`bookings.${index}.shipFrom.transitDetails.modeOfTransit`)

  useEffect(() => {
    if (modeOfTransit > -1) return
    if (selectedLocation?.defaultTransportModeId) {
      setValue(
        `bookings.${index}.shipFrom.transitDetails.modeOfTransit`,
        selectedLocation?.defaultTransportModeId
      )
    } else {
      setValue(`bookings.${index}.shipFrom.transitDetails.modeOfTransit`, -1)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocation])

  const contactId = watch(`bookings.${index}.shipFrom.contactId`)
  const specialInstructions = watch(`bookings.${index}.shipFrom.specialInstructions`)
  const clearingRequired = watch(`bookings.${index}.shipFrom.clearingRequired`)
  const emptyContainerCollection = watch(`bookings.${index}.shipFrom.emptyContainerCollection`)
  const returnTo = watch(`bookings.${index}.shipFrom.returnTo`)
  const address =
    getLocationAddress(locations, locationId) !== 'N/A'
      ? getLocationAddress(locations, locationId)
      : embeddedLocation?.address?.address1 ?? 'N/A'
  const getContactName = (contactId: number) => {
    const contactOptions = getContactOptions() || []
    const contact = contactOptions.find((c: any) => c.value === contactId)
    if (contact) return contact.label
    const embeddedContact = embeddedLocation?.contacts?.find((c: any) => c.id === contactId)
    if (embeddedContact) {
      return `${embeddedContact.contactType?.name} - ${embeddedContact.name}`
    }
    return 'N/A'
  }

  return (
    <>
      {ModalComponent}
      <EmptyContainerCollection consignmentIndex={index} disabled={disabled} />
      <FlowSection>
        <StyledAccordion
          sx={theme => ({ backgroundColor: theme.palette?.accordion?.parent?.bg, marginTop: '10px' })}
          defaultExpanded
          onChange={handleToggleAccordion}
          title={formatMessage(messages.shipFrom)}
        >
          <StyledAccordionSummary
            index={`shipfrom-content-${index}`}
            expanded={expanded}
          >
            <Grid container>
              <Grid item xs={12} md={3} sx={{ display: 'flex', justifyContent: 'flex-start', mr: '-76px' }}>
                <LabelItem>{formatMessage(messages.shipFrom)}</LabelItem>
              </Grid>
              {!expanded && (
                <>
                  <Grid item xs={12} md={3} sx={{ display: 'flex', justifyContent: 'flex-start', mr: '44px' }}>
                    <InfoDisplayItem
                      label={formatMessage(messages.shipFrom)}
                      value={getLocationName(locationId)}
                      isSmallScreen={false}
                    />
                  </Grid>
                  <Grid item xs={12} md={4} sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                    <InfoDisplayItem
                      label={formatMessage(messages.requestedDate)}
                      value={loadingDate}
                      isSmallScreen={false}
                    />
                  </Grid>
                </>
              )}
            </Grid>

          </StyledAccordionSummary>
          <AccordionDetails>
            {variant === 'summary' ? (
              <DefaultSpacingGrid container sx={{ pb: '8px' }}>
                <Grid item xs={12} md={6} sx={{ mr: '-28px' }}>
                  <InfoDisplayItem
                    label={formatMessage(messages.location)}
                    value={getLocationName(locationId)}
                    isSmallScreen={false}
                    allowOverlap={true}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <InfoDisplayItem
                    label={formatMessage(messages.requestedDate)}
                    value={loadingDate}
                    isSmallScreen={false}
                    allowOverlap={true}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <InfoDisplayItem
                    label={formatMessage(messages.contact)}
                    value={getContactName(contactId)}
                    isSmallScreen={false}
                    allowOverlap={true}
                  />
                </Grid>
                <Grid item xs={12} md={6} sx={{ mr: '-28px' }}>
                  <InfoDisplayItem
                    label={formatMessage(messages.address)}
                    value={address || 'N/A'}
                    isSmallScreen={false}
                    allowOverlap={true}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <InfoDisplayItem
                    label={formatMessage(messages.specialInstructions)}
                    value={specialInstructions || 'N/A'}
                    isSmallScreen={false}
                    allowOverlap={true}
                  />
                </Grid>
                <Grid item xs={12} md={3} sx={{ mr: '-28px' }}>
                  <InfoDisplayItem
                    label={formatMessage(messages.clearingRequired)}
                    value={clearingRequired ? 'Yes' : 'No'}
                    isSmallScreen={false}
                    allowOverlap={true}
                  />
                </Grid>
                {loadTypeId === LoadType.container && (
                  <Grid item xs={12} md={3} sx={{ ml: '-40px' }}>
                    <InfoDisplayItem
                      label={formatMessage(messages.useEmptyContainerCollection)}
                      value={emptyContainerCollection ? 'Yes' : 'No'}
                      isSmallScreen={false}
                      allowOverlap={true}
                    />
                  </Grid>
                )}
                <Grid item xs={12} md={6} sx={{ ml: '40px' }}>
                  <InfoDisplayItem
                    label={formatMessage(messages.returnTo)}
                    value={returnTo ? formatMessage(messages.different) : formatMessage(messages.sameAsDefault)}
                    isSmallScreen={false}
                    allowOverlap={true}
                  />
                </Grid>
              </DefaultSpacingGrid>
            ) : (
              <DefaultSpacingGrid container>
                <Grid item xs={12} md={6}>
                  <FormSelect
                    name={`bookings.${index}.shipFrom.locationId`}
                    label={formatMessage(messages.location)}
                    options={getAvailableLocations(locationOptions, usedLocationIds)}
                    allOptions={locationOptions}
                    required={true}
                    onChange={(e) => {
                      setValue(`bookings.${index}.shipFrom.contactId`, null)
                      const selectedId = +e.target.value
                      const revision = getLocationRevision(locations, selectedId)
                      setValue(`bookings.${index}.shipFrom.locationRevision`, revision)
                      const selectedLocation = locations.find((l: any) => l.id === selectedId)
                      setValue(`bookings.${index}.shipFrom.location`, selectedLocation ?? undefined)
                    }}
                    actionLabel={
                      !disabled ? (
                        <ActionButton
                          onClick={() => {
                            openModal()
                          }}
                          label={formatMessage(commonMessages.addNew)}
                        />
                      ) : undefined
                    }
                    fullWidth
                    disabled={disabled}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <FormDatePickerInput
                    name={`bookings.${index}.shipFrom.loadingDate`}
                    label={formatMessage(messages.requestedDate)}
                    format='YYYY-MM-DD HH:mm'
                    required={true}
                    disabled={disabled}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormSelect
                    name={`bookings.${index}.shipFrom.contactId`}
                    label={formatMessage(messages.contact)}
                    disableLabel={formatMessage(messages.noContactsAvailable)}
                    options={getContactOptions()}
                    actionLabel={
                      !disabled ? (
                        <ActionButton
                          onClick={() => {
                            setValue(`bookings.${index}.shipFrom.contactId`, null)
                          }}
                          label={formatMessage(messages.clearSelection)}
                        />
                      ) : undefined
                    }
                    fullWidth
                    disabled={disabled}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box display='flex' alignItems='center' justifyContent='space-between'>
                    <Typography variant='subtitle1' gutterBottom sx={{ color: '#636363' }}>
                      {formatMessage(messages.address)}
                    </Typography>
                    {locationId && !disabled && (
                      <Button
                        onClick={() => openModal(selectedLocation)}
                        size='small'
                        variant='text'
                        sx={{
                          textTransform: 'none',
                          minWidth: 'auto',
                          ml: 1,
                          color: '#3D7FDA',
                          fontWeight: 'bold',
                          '&:hover': {
                            backgroundColor: 'transparent',
                            textDecoration: 'underline',
                          },
                        }}
                        endIcon={<EditLocationAltIcon fontSize='small' sx={{ color: 'inherit' }} />}
                      >
                        {formatMessage(commonMessages.editBtn)}
                      </Button>
                    )}
                  </Box>
                  <Typography variant='body2' sx={{ color: '#636363' }}>
                    {getLocationAddress(locations, locationId)}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormInput
                    name={`bookings.${index}.shipFrom.specialInstructions`}
                    label={formatMessage(messages.specialInstructions)}
                    placeholder={formatMessage(commonMessages.pleaseEnter)}
                    fullWidth
                    disabled={disabled}
                    maxLength={200}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      height: '100%',
                    }}
                  >
                    <Controller
                      name={`bookings.${index}.shipFrom.clearingRequired`}
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={
                            <Switch
                              checked={!!field.value}
                              onChange={(e) => {
                                field.onChange(e.target.checked)
                                trigger(`bookings.${index}.clearingRequired`)
                              }}
                              disabled={disabled}
                            />
                          }
                          label={formatMessage(messages.clearingRequired)}

                        />
                      )}
                    />
                  </Box>
                </Grid>
                {loadTypeId === LoadType.container && (
                  <Grid item xs={12} md={4}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        height: '100%',
                      }}
                    >
                      <Controller
                        name={`bookings.${index}.shipFrom.emptyContainerCollection`}
                        control={control}
                        render={({ field }) => (
                          <FormControlLabel
                            control={
                              <Switch
                                checked={!!field.value}
                                onChange={(e) => {
                                  field.onChange(e.target.checked)
                                  if (!e.target.checked) {
                                    setValue(`bookings.${index}.containerCollections`, undefined)
                                  }
                                }}
                                disabled={disabled}
                              />
                            }
                            label={formatMessage(messages.useEmptyContainerCollection)}
                          />
                        )}
                      />
                    </Box>
                  </Grid>
                )}

                <Grid item xs={12} md={4}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                    }}
                  >
                    <Typography variant='subtitle1' sx={{ textAlign: 'left', color: '#636363' }}>
                      {formatMessage(messages.returnTo)}
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Typography sx={{ marginRight: 1, fontSize: '12px', color: '#636363' }}>
                        {formatMessage(messages.sameAsDefault)}
                      </Typography>
                      <Controller
                        name={`bookings.${index}.shipFrom.returnTo`}
                        control={control}
                        render={({ field }) => (
                          <Switch
                            checked={!!field.value}
                            onChange={(e) => {
                              field.onChange(e.target.checked)
                              if (!e.target.checked) {
                                setValue(`bookings.${index}.returnTo`, undefined)
                              }
                            }}
                            disabled={disabled || isCreated}
                          />
                        )}
                      />
                      <Typography sx={{ marginLeft: 1, fontSize: '12px', color: '#636363' }}>
                        {formatMessage(messages.different)}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} md={12} sx={{ pl: '0 !important' }}>
                  <ReturnTo index={index} disabled={disabled} />
                </Grid>
              </DefaultSpacingGrid>
            )}
          </AccordionDetails>
        </StyledAccordion>
      </FlowSection>
      <WaypointContainer consignmentIndex={index} disabled={disabled} />
    </>
  )
}

export default ShipFromForm
